import { useQuery } from '@tanstack/react-query'
import { setCookie, parseCookies, destroyCookie } from 'nookies'
import { toast } from 'react-toastify'

import { getIntercomHash } from './intercom'

import api from 'services/api'

import { queryClient } from 'providers/QueryClientProvider'

import { withDelayedRetryBackoff } from 'utils/backoff'
import { deleteDataContractId } from 'utils/helpers'
import {
	setSearchVersionCookie,
	getSearchVersionFromUser
} from 'utils/searchVersionCheck'

export const getDefaultCookieOptions = () => ({
	path: '/',
	sameSite: 'Lax',
	secure: process.env.NODE_ENV === 'production',
	domain: process.env.NODE_ENV === 'production' && '.vittude.com'
})

// Chamada do /me e retorna a resposta
export const getUser = async () => {
	// get logalStorage vittude-profile
	const user = getProfile()

	const cookies = parseCookies()
	if (!cookies?.['@vittude:user']) {
		setCookie(
			null,
			'@vittude:user',
			JSON.stringify({
				contract: user?.contract,
				id: user?.id
			}),
			{
				...getDefaultCookieOptions(),
				maxAge: 30 * 24 * 60 * 60 // 30 dias
			}
		)
	}

	try {
		const userMe = await api.get('/me/')

		const searchVersion = getSearchVersionFromUser(userMe?.data?.content?.user)
		if (searchVersion) {
			setSearchVersionCookie(userMe?.data?.content?.user)
		}

		const userIntercomHash = await withDelayedRetryBackoff(
			() => getIntercomHash(),
			{
				timeoutInMilliseconds: 500
			}
		)

		if (userIntercomHash?.data?.content?.intercom_hash) {
			userMe.data.content.user.intercom_hash =
				userIntercomHash.data.content.intercom_hash
		}

		return userMe
	} catch (error) {
		console.log('Catch do getUser /me.', error)
		throw error
	}
}

export const useMe = (options = {}) => {
	const profile = getProfile()
	return useQuery({
		queryKey: ['get-me'],
		queryFn: async () => await getUser(), // chamada do /me
		initialData: profile,
		enabled: !!profile,
		select: (data = {}) => data?.data?.content?.user ?? null,
		onSuccess: async (patient) => {
			if (patient) {
				await options?.onSuccess?.()
				setProfile(patient)
				if (patient?.contract) {
					setCookie(null, 'contractId', patient?.contract.id, {
						...getDefaultCookieOptions(),
						maxAge: 24 * 60 * 60 // 1 day
					})
				}
			}
		},
		...options
	})
}

export const setProfile = (profile) => {
	try {
		if (!process.browser) {
			return
		}
		localStorage.setItem('vittude-profile', JSON.stringify(profile))
	} catch (error) {
		console.error('Error setting profile:', error)
	}
}

// get logalStorage vittude-profile
export const getProfile = () => {
	try {
		if (!process.browser) {
			return null
		}
		return JSON.parse(localStorage.getItem('vittude-profile'))
	} catch (error) {
		console.error('Error getting profile:', error)
		return null
	}
}

export const getIsSsoUserByCpf = (cpf) =>
	api.get('/verify_sso_user/', { params: { cpf } })

export const getTotpStatus = () => api.get('/totp/')

export const postTotp = async (verifyCode) => {
	const response = await api.post('/totp/', { totp: verifyCode })

	if (response?.data?.content?.vittude_totp) {
		setCookie(null, 'vittude-totp', response.data.content.vittude_totp, {
			...getDefaultCookieOptions(),
			maxAge: 90 * 24 * 60 * 60 // 90 days
		})
	}

	return response.data
}

/* Novo fluxo de autenticação */

export const logout = async () => {
	try {
		const userData = queryClient.getQueryData(['get-me'])
		await api.post('/logout/', { ...CREDENTIALS })

		// entender se esse evento é utilizado
		try {
			await api.post('/event_messenger/notify/', {
				patient_id: userData?.data?.content?.user?.id,
				event_type: 'signout'
			})
		} catch (error) {
			console.error('Error notifying logout:', error)
		}
	} finally {
		if (process.browser) {
			clear()
			toast.success('Você saiu da conta.', {
				autoClose: 3000,
				hideProgressBar: false,
				closeButton: true
			})
		}
	}
}

export const clear = async ({ includeProfile } = { includeProfile: true }) => {
	if (typeof window === 'undefined') {
		return
	}

	if (includeProfile) {
		localStorage.removeItem('vittude-profile')
	}

	// Limpa auth cookies httpOnly através do server-side
	try {
		await fetch('/api/auth/clear-cookies', {
			method: 'POST'
		})
	} catch (err) {
		console.error('Erro ao limpar cookies HttpOnly', err)
	}

	const cookieOptions = getDefaultCookieOptions()
	destroyCookie(null, '@vittude:user', cookieOptions)
	destroyCookie(null, 'vittude-totp', cookieOptions)
	destroyCookie(null, 'searchVersion', cookieOptions)
	deleteDataContractId()
}

export const CREDENTIALS = {
	client_id: 'Uc0q80e1BMmqWqLYFZVuPM7WtEZWWELfCpN1qe8m',
	client_secret:
		'dXhutPqPeoJVgH46escNl7cE5of9sWCJULl6WbfYemAMA4p36TNoGA0VXkln3pOOtbfrWzntBy8E3GuaYSEClZJM07tVjf7PJlGynKvwOAd1aoq4MqTvSKllx9Ho3xlJ'
}

export const authCookieOptions = {
	path: '/',
	secure: true,
	httpOnly: true,
	sameSite: 'none',
	domain: '.vittude.com'
}

export const setUserAuthCookies = ({ res, access_token, refresh_token }) => {
	const maxAge = 90 * 24 * 60 * 60 // 90 dias

	setCookie({ res }, 'vittude-token', access_token, {
		...authCookieOptions,
		maxAge
	})

	setCookie({ res }, 'vittude-refresh-token', refresh_token, {
		...authCookieOptions,
		maxAge
	})
}

export const reAuthenticateByRefreshToken = async () => {
	const payload = {
		client_id: CREDENTIALS.client_id,
		client_secret: CREDENTIALS.client_secret,

		grant_type: 'refresh_token',
		name: 'WebApp'
	}

	const response = await fetch('/api/auth/login', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(payload)
	})

	return response
}

export const loginUserByGetToken = async ({ username, password }) => {
	const payload = {
		client_id: CREDENTIALS.client_id,
		client_secret: CREDENTIALS.client_secret,

		grant_type: 'password',
		name: 'WebApp',

		username,
		password
	}

	const response = await fetch('/api/auth/login', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload)
	})

	const data = await response.json()

	if (!response.ok) {
		const error = new Error(data?.message)
		error.response = { status: response.status, data }

		throw error
	}

	await queryClient.fetchQuery(['get-me'], { staleTime: 0 })

	return response
}

export const loginUserByAlternativeWay = async (accessToken, refreshToken) => {
	if (accessToken && refreshToken) {
		const response = await fetch('/api/auth/login-sso', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				access_token: accessToken,
				refresh_token: refreshToken
			})
		})

		if (response.ok) {
			await queryClient.fetchQuery(['get-me'], { staleTime: 0 })
		}

		return response
	}
	return 'Usuário já autenticado ou não possui tokens válidos.'
}

// TODO: VERIFICAR e alterar await queryClient.fetchQuery(['get-me'], {staleTime: 0}) pela chamada da função refetchFreshUserMe()
// export const refetchFreshUserMe = await queryClient.fetchQuery(['get-me'], {
// 	staleTime: 0
// })
